import { useState } from 'react'
import Button from '@mui/material/Button'
import { useFlags } from '@equipmentshare/feature-flags'
import { PickableFulfillmentOrder, isPickableFulfillmentOrder } from '../selection'
import useCanCreatePickOrder from '../useCanCreatePickOrder'
import CreatePickOrderDialog from './CreatePickOrderDialog'

export type CreatePickOrderButtonProps = {
	fulfillmentOrder: PickableFulfillmentOrder & {
		id: string,
		orderNumber: number,
		/**
		 * The DC that will fill this order — read from the order itself, never from
		 * whichever DC the DC Admin header happens to be showing. This page is
		 * reached by order number and is not scoped to that DC, so the two disagree
		 * routinely, and a run created against the wrong one is rejected.
		 */
		fulfillingInventoryId: string,
		destinationDescription: string | null,
	},
	size?: 'small' | 'medium' | 'large',
}

/**
 * Starts a pick order from one fulfillment order, on that order's own page.
 *
 * The table has its own way in — check the orders, then use the action above
 * the grid — because a run is usually several orders sharing a destination.
 * Here there is only one order in view, so it is the whole run; more can be
 * folded in from the pick order itself.
 *
 * Visible only when the pick-order area is flagged on AND the user holds
 * CAN_CREATE_PICK_ORDER_BY_STORE_ID at the order's fulfilling DC AND the order
 * is in a state that can still be picked.
 */
export default function CreatePickOrderButton( { fulfillmentOrder, size = 'small' }: CreatePickOrderButtonProps ) {
	const [ dialogOpen, setDialogOpen ] = useState( false )
	const flags = useFlags()
	const canCreatePickOrder = useCanCreatePickOrder( fulfillmentOrder.fulfillingInventoryId )

	if ( !flags.costentoryEnablePickOrders || !canCreatePickOrder || !isPickableFulfillmentOrder( fulfillmentOrder ) ) {
		return null
	}

	return (
		<>
			<Button variant="outlined" size={size} onClick={() => setDialogOpen( true )}>
				Create Pick Order
			</Button>
			{dialogOpen && (
				<CreatePickOrderDialog
					open
					onClose={() => setDialogOpen( false )}
					fulfillingInventoryId={fulfillmentOrder.fulfillingInventoryId}
					orders={[ { id: fulfillmentOrder.id, orderNumber: fulfillmentOrder.orderNumber } ]}
					destinationDescription={fulfillmentOrder.destinationDescription}
				/>
			)}
		</>
	)
}
